/**
 * Mirrors iOS CruiseCountdownCalculator.
 */

export type CruiseCountdownDisplayMode = 'yearMonthDay' | 'monthDayHour' | 'dayHourMinute';

export interface CruiseCountdownUnit {
  value: number;
  labelKey: 'years' | 'months' | 'days' | 'hours' | 'minutes';
}

export interface CruiseCountdownSnapshot {
  mode: CruiseCountdownDisplayMode;
  units: CruiseCountdownUnit[];
  hasDeparted: boolean;
  /** Suggested refresh interval in ms */
  updateIntervalMs: number;
}

const MONTH_DAY_HOUR_THRESHOLD_MS = 30 * 24 * 60 * 60 * 1000;

const DEPARTED: CruiseCountdownSnapshot = {
  mode: 'dayHourMinute',
  units: [],
  hasDeparted: true,
  updateIntervalMs: 60_000,
};

function calendarDiff(
  from: Date,
  to: Date,
  units: Array<'year' | 'month' | 'day' | 'hour' | 'minute'>
): Record<string, number> {
  // Approximate calendar components the way iOS Calendar.dateComponents does for display.
  let cursor = new Date(from.getTime());
  const result: Record<string, number> = {};

  if (units.includes('year')) {
    let years = to.getFullYear() - cursor.getFullYear();
    const probe = new Date(cursor);
    probe.setFullYear(probe.getFullYear() + years);
    if (probe > to) years -= 1;
    years = Math.max(0, years);
    cursor.setFullYear(cursor.getFullYear() + years);
    result.year = years;
  }

  if (units.includes('month')) {
    let months =
      (to.getFullYear() - cursor.getFullYear()) * 12 + (to.getMonth() - cursor.getMonth());
    const probe = new Date(cursor);
    probe.setMonth(probe.getMonth() + months);
    if (probe > to) months -= 1;
    months = Math.max(0, months);
    cursor.setMonth(cursor.getMonth() + months);
    result.month = months;
  }

  if (units.includes('day')) {
    const ms = to.getTime() - cursor.getTime();
    const days = Math.max(0, Math.floor(ms / 86_400_000));
    cursor.setDate(cursor.getDate() + days);
    result.day = days;
  }

  if (units.includes('hour')) {
    const ms = to.getTime() - cursor.getTime();
    const hours = Math.max(0, Math.floor(ms / 3_600_000));
    cursor.setHours(cursor.getHours() + hours);
    result.hour = hours;
  }

  if (units.includes('minute')) {
    const ms = to.getTime() - cursor.getTime();
    result.minute = Math.max(0, Math.floor(ms / 60_000));
  }

  return result;
}

export function cruiseCountdownDisplayMode(now: Date, target: Date): CruiseCountdownDisplayMode {
  const yearDiff = calendarDiff(now, target, ['year']).year ?? 0;
  if (yearDiff >= 1) return 'yearMonthDay';
  if (target.getTime() - now.getTime() > MONTH_DAY_HOUR_THRESHOLD_MS) return 'monthDayHour';
  return 'dayHourMinute';
}

export function cruiseCountdownUnits(
  mode: CruiseCountdownDisplayMode,
  now: Date,
  target: Date
): CruiseCountdownUnit[] {
  switch (mode) {
    case 'yearMonthDay': {
      const c = calendarDiff(now, target, ['year', 'month', 'day']);
      return [
        { value: c.year ?? 0, labelKey: 'years' },
        { value: c.month ?? 0, labelKey: 'months' },
        { value: c.day ?? 0, labelKey: 'days' },
      ];
    }
    case 'monthDayHour': {
      const c = calendarDiff(now, target, ['month', 'day', 'hour']);
      return [
        { value: c.month ?? 0, labelKey: 'months' },
        { value: c.day ?? 0, labelKey: 'days' },
        { value: c.hour ?? 0, labelKey: 'hours' },
      ];
    }
    case 'dayHourMinute': {
      const c = calendarDiff(now, target, ['day', 'hour', 'minute']);
      return [
        { value: c.day ?? 0, labelKey: 'days' },
        { value: c.hour ?? 0, labelKey: 'hours' },
        { value: c.minute ?? 0, labelKey: 'minutes' },
      ];
    }
  }
}

export function cruiseCountdownSnapshot(now: Date, target: Date): CruiseCountdownSnapshot {
  if (target.getTime() <= now.getTime()) return DEPARTED;
  const mode = cruiseCountdownDisplayMode(now, target);
  return {
    mode,
    units: cruiseCountdownUnits(mode, now, target),
    hasDeparted: false,
    updateIntervalMs: mode === 'dayHourMinute' ? 1_000 : 60_000,
  };
}

/** Parse YYYY-MM-DD as local start-of-day for countdown target. */
export function parseTripStartLocal(isoDate: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate.trim());
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 0, 0, 0, 0);
}
